// Host-exec accept-side manager.
//
// When a relay-paired client sends a hello with `mode: "host-exec"`,
// `websocket-server.handleHello` hands the connection here instead of through
// `createSessionConnection`. We:
//
//   1. Re-verify the wsToken locally (defence-in-depth — the dialer already
//      passed the auth-server's `canUserAccessHost` predicate at token-mint
//      time, but the daemon independently confirms the token is bound to this
//      serverId and this peer pubkey).
//   2. Spawn the owner's login shell running the requested command
//      (`<shell> -lc <command>` on POSIX, `cmd.exe /d /s /c <command>` on
//      Windows), the same shell the in-app terminal grants.
//   3. Stream stdout/stderr back as JSON frames over the encrypted relay
//      channel and, on exit, send a final `exit` frame, then close.
//
// This is the non-interactive analogue of the in-app terminal: an agent on a
// sibling host runs a command here and reads clean stdout/stderr + an exit
// code — exactly what its local Bash tool gives it, pointed at this host.
//
// Like tcp-forward, a host-exec connection does NOT allocate a Session, does
// NOT run `sendInitialState`, and lives in its own map. There is no inbound
// data leg — the command arrives in the hello, so we never read frames from
// the dialer after accept; we only stream output back.

import { type ChildProcessByStdio, spawn } from "node:child_process";
import os from "node:os";
import type { Readable } from "node:stream";
import type { Logger } from "pino";

// stdin is `ignore`d (null), stdout/stderr are piped Readables. Capturing the
// exact stdio shape keeps `child.stdout`/`child.stderr` non-nullable.
type HostExecChild = ChildProcessByStdio<null, Readable, Readable>;

import { resolveDefaultTerminalShell } from "../../terminal/terminal.js";
import { verifyWsJwt } from "../ws-jwt.js";
import {
  HOST_EXEC_DEFAULT_TIMEOUT_MS,
  HOST_EXEC_MAX_OUTPUT_BYTES,
  HOST_EXEC_MAX_TIMEOUT_MS,
  type HostExecFrame,
} from "./host-exec-protocol.js";

// Minimal WS shape we need (mirrors `WebSocketLike` in tcp-forward-manager.ts).
// The outbound `send` here is the encrypted-channel wrapper, so a string we
// send is encrypted and delivered to the dialer as a string frame.
export interface WebSocketLike {
  readyState: number;
  send: (data: string | Uint8Array | ArrayBuffer) => void;
  close: (code?: number, reason?: string) => void;
}

interface HostExecConnection {
  ws: WebSocketLike;
  child: HostExecChild;
  logger: Logger;
  timeout: NodeJS.Timeout | null;
  bytesSent: number;
  truncated: boolean;
  finished: boolean;
}

export interface HostExecAcceptArgs {
  ws: WebSocketLike;
  logger: Logger;
  peerPublicKeyB64: string | null;
  wsToken: string;
  exec: { command: string; cwd?: string; timeoutMs?: number } | undefined;
  serverPubkeyHex: string | null;
  serverId: string | null;
}

export class HostExecManager {
  private readonly connections = new Map<WebSocketLike, HostExecConnection>();

  isHostExec(ws: WebSocketLike): boolean {
    return this.connections.has(ws);
  }

  accept(args: HostExecAcceptArgs): void {
    const { ws, peerPublicKeyB64, wsToken, exec } = args;
    const log = args.logger.child({ module: "host-exec" });

    if (!args.serverPubkeyHex) {
      log.warn("host_exec_rejected_daemon_not_linked");
      this.closeWs(ws, 1008, "auth_required: daemon not linked");
      return;
    }

    const jwt = verifyWsJwt(wsToken, args.serverPubkeyHex, peerPublicKeyB64, args.serverId ?? null);
    if (!jwt.ok) {
      log.warn({ reason: jwt.reason }, "host_exec_rejected_invalid_jwt");
      this.closeWs(ws, 1008, `auth_required: ${jwt.reason}`);
      return;
    }

    if (!exec || !exec.command) {
      log.warn("host_exec_rejected_missing_command");
      this.closeWs(ws, 1008, "missing_command");
      return;
    }

    const timeoutMs = Math.min(
      exec.timeoutMs ?? HOST_EXEC_DEFAULT_TIMEOUT_MS,
      HOST_EXEC_MAX_TIMEOUT_MS,
    );
    const shell = resolveDefaultTerminalShell();
    const cwd = exec.cwd && exec.cwd.length > 0 ? exec.cwd : os.homedir();

    log.info({ ownerUserId: jwt.userId, cwd, timeoutMs }, "host_exec_accept");

    // On Windows the resolved shell is cmd.exe (ComSpec), which doesn't
    // understand `-lc`. `/d /s /c` mirrors what Node's `shell: true` does:
    // skip AutoRun, strip outer quotes, run the command, exit. With
    // `windowsVerbatimArguments` the command string reaches cmd.exe unmangled.
    const shellArgs =
      process.platform === "win32" ? ["/d", "/s", "/c", exec.command] : ["-lc", exec.command];

    let child: HostExecChild;
    try {
      child = spawn(shell, shellArgs, {
        cwd,
        env: process.env,
        // Keep stdin open but unused; capture stdout/stderr as pipes.
        stdio: ["ignore", "pipe", "pipe"],
        ...(process.platform === "win32"
          ? { windowsVerbatimArguments: true, windowsHide: true }
          : {}),
      });
    } catch (err) {
      log.warn({ err }, "host_exec_spawn_failed");
      this.sendFrame(ws, { type: "error", message: `spawn failed: ${String(err)}` });
      this.closeWs(ws, 1011, "spawn_failed");
      return;
    }

    const conn: HostExecConnection = {
      ws,
      child,
      logger: log,
      timeout: null,
      bytesSent: 0,
      truncated: false,
      finished: false,
    };
    this.connections.set(ws, conn);

    conn.timeout = setTimeout(() => {
      log.warn({ timeoutMs }, "host_exec_timeout");
      conn.truncated = true;
      this.killChild(conn);
    }, timeoutMs);

    child.stdout.on("data", (chunk: Buffer) => this.onOutput(conn, "stdout", chunk));
    child.stderr.on("data", (chunk: Buffer) => this.onOutput(conn, "stderr", chunk));

    child.on("error", (err) => {
      log.warn({ err }, "host_exec_child_error");
      this.sendFrame(ws, { type: "error", message: String(err) });
      this.finish(conn, null, null);
    });

    child.on("close", (code, signal) => {
      this.finish(conn, code, signal);
    });
  }

  private onOutput(conn: HostExecConnection, stream: "stdout" | "stderr", chunk: Buffer): void {
    if (conn.finished) return;
    const remaining = HOST_EXEC_MAX_OUTPUT_BYTES - conn.bytesSent;
    if (remaining <= 0) {
      if (!conn.truncated) {
        conn.truncated = true;
        this.killChild(conn);
      }
      return;
    }
    const slice = chunk.length > remaining ? chunk.subarray(0, remaining) : chunk;
    conn.bytesSent += slice.length;
    this.sendFrame(conn.ws, {
      type: stream,
      dataB64: slice.toString("base64"),
    });
    if (conn.bytesSent >= HOST_EXEC_MAX_OUTPUT_BYTES && !conn.truncated) {
      conn.truncated = true;
      this.killChild(conn);
    }
  }

  /** Called from `handleClose` to free the child if the WS drops first. */
  handleWsClose(ws: WebSocketLike): boolean {
    const conn = this.connections.get(ws);
    if (!conn) return false;
    if (conn.timeout) clearTimeout(conn.timeout);
    conn.finished = true;
    this.connections.delete(ws);
    this.killChild(conn);
    return true;
  }

  private finish(conn: HostExecConnection, code: number | null, signal: string | null): void {
    if (conn.finished) return;
    conn.finished = true;
    if (conn.timeout) {
      clearTimeout(conn.timeout);
      conn.timeout = null;
    }
    this.connections.delete(conn.ws);
    if (conn.truncated) {
      this.sendFrame(conn.ws, {
        type: "error",
        message: `output or runtime limit exceeded (max ${HOST_EXEC_MAX_OUTPUT_BYTES} bytes)`,
      });
    }
    this.sendFrame(conn.ws, { type: "exit", code, signal });
    this.closeWs(conn.ws, 1000, "host_exec_done");
  }

  private killChild(conn: HostExecConnection): void {
    // On Windows there are no signals — both kill() calls below terminate the
    // shell process directly (grandchildren may survive; acceptable for the
    // timeout/limit path, matching how the rest of the daemon kills children).
    try {
      conn.child.kill("SIGTERM");
    } catch {
      // ignore
    }
    // Hard-kill if SIGTERM didn't land promptly.
    setTimeout(() => {
      try {
        if (conn.child.exitCode === null && conn.child.signalCode === null) {
          conn.child.kill("SIGKILL");
        }
      } catch {
        // ignore
      }
    }, 2000);
  }

  private sendFrame(ws: WebSocketLike, frame: HostExecFrame): void {
    try {
      ws.send(JSON.stringify(frame));
    } catch {
      // ignore — channel may already be closing
    }
  }

  private closeWs(ws: WebSocketLike, code: number, reason: string): void {
    try {
      ws.close(code, reason);
    } catch {
      // ignore
    }
  }
}
